import React, { useState, useMemo, useRef, useEffect } from 'react';
import { VStack, HStack, Box, Button, Text, Input, NativeSelectRoot, NativeSelectField, Checkbox, Image } from '@chakra-ui/react';
import { Field } from '@chakra-ui/react';
import { useFormContext } from '../context/FormContext';
import { DonationContinueSection } from '../DonationContinueSection';
import { prevStep, setPaymentMethod, setDonorInfo, setReceipt, setSubmitting, setSubmissionError } from '../context/formActions';
import { canContinueFromStep, validateInformationField, type InformationFieldErrors } from '../validation';
import type { DonorInfo } from '../types';
import { formatUsPhone, formatUsPhoneAsYouType, getCountryOptions, getUSStates, getCAProvinces, normalizePhoneDigits } from '@kuow/core';
import { Recaptcha, type RecaptchaHandle } from '../../Recaptcha';
import { submitDonation, submitDonationUpgrade } from '../services/donationSubmission';
import { DonationSubmissionResult, logger, donateEvent, purchaseEvent } from '@kuow/core';
import { useProcessingMessage } from '../../../hooks/useProcessingMessage';
import charityLogo from '../../../assets/images/charity.png';
import verisignLogo from '../../../assets/images/verisign.png';
import { useDonationFormUi } from '../context/UiContext';
import {
  getPrimarySubmissionError,
  getSubmissionErrorMessage,
  isPaymentFailureError,
  SUBMISSION_ERROR_FALLBACK_MESSAGE,
} from '../utils/submissionErrors';

export const InformationStep: React.FC = () => {
  const { state, dispatch } = useFormContext();
  const { showStepNavigator } = useDonationFormUi();
  const { formData, validation, isSubmitting, submissionError } = state;
  
  const hasGift = formData.gift !== null && !formData.noGift;
  const alwaysShowAddress = Boolean(state.formDetails?.always_show_address);
  const showAddressSection = hasGift || alwaysShowAddress;
  const isCallCenterForm = state.formDetails?.kind === 'call_center';
  const isEmailRequired = !isCallCenterForm;
  const isMonthlyUpgrade = state.mode === 'monthly-upgrade';
  const isPaypalRest = formData.paymentMethod === 'paypal';
  const submissionInFlight = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<InformationFieldErrors>({});
  const captchaRef = useRef<RecaptchaHandle>(null);
  const processingMessage = useProcessingMessage(isSubmitting);

  const countryOptions = useMemo(() => getCountryOptions(), []);
  const stateOptions = useMemo(() => {
    return formData.country === 'Canada' ? getCAProvinces() : getUSStates();
  }, [formData.country]);

  useEffect(() => {
    donateEvent('Step Information', state.placement);
  }, [state.placement]);

  // Determine if captcha is required based on payment method
  const requiresCaptcha = !state.disableCaptcha && (formData.paymentMethod === 'card' || formData.paymentMethod === 'bank');

  // Execute captcha on component mount if required
  useEffect(() => {
    if (requiresCaptcha && captchaRef.current) {
      void captchaRef.current.execute().catch((error: unknown) => {
        logger.error('reCAPTCHA execution failed on mount', { error });
      });
    }
  }, [requiresCaptcha]);

  const handleFieldChange = <K extends keyof DonorInfo>(field: K, value: DonorInfo[K]) => {
    if (submissionError) {
      dispatch(setSubmissionError(null));
    }
    // Send only the changed fields. This handler can run from an async callback
    // (the reCAPTCHA token) holding an older render's formData, and resending
    // every field from that snapshot would overwrite newer input such as autofill.
    const updates: Partial<DonorInfo> = { [field]: value };

    // Clear state when country changes since valid options are different
    if (field === 'country') {
      updates.state = '';
    }

    dispatch(setDonorInfo(updates));
    // Clear error for this field when user starts typing
    if (field !== 'phone' && field !== 'newsletterOptIn' && field !== 'captcha' && fieldErrors[field as keyof InformationFieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Clear state error when country changes
    if (field === 'country' && fieldErrors.state) {
      setFieldErrors((prev) => ({ ...prev, state: undefined }));
    }
  };

  const handlePhoneChange = (value: string) => {
    handleFieldChange('phone', formatUsPhoneAsYouType(value));
  };

  const handlePhoneBlur = (value: string) => {
    const digits = normalizePhoneDigits(value);
    handleFieldChange('phone', digits ? formatUsPhone(value) : '');
  };

  const handleCaptchaVerified = (token: string) => {
    handleFieldChange('captcha', token);
  };

  const handleCaptchaError = () => {
    handleFieldChange('captcha', '');
    logger.error('reCAPTCHA verification failed');
  };

  const handleFieldBlur = (field: string, value: string) => {
    const error = validateInformationField(field, value, showAddressSection, isEmailRequired);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  useEffect(() => {
    if (isEmailRequired || !fieldErrors.email) return;
    setFieldErrors((prev) => ({ ...prev, email: undefined }));
  }, [fieldErrors.email, isEmailRequired]);

  const handleSubmit = async () => {
    if (submissionInFlight.current || isSubmitting || formData.paypalCompletionUncertain || !canContinueFromStep('information', validation)) {
      return;
    }

    if (!state.formDetails) {
      logger.error('Cannot submit donation without form details');
      return;
    }

    submissionInFlight.current = true;
    dispatch(setSubmissionError(null));
    dispatch(setSubmitting(true));

    try {
      const result: DonationSubmissionResult = await (state.mode === 'monthly-upgrade'
        ? (() => {
            if (!state.activeSubscription || !state.upgradeJwt) {
              logger.error('Cannot submit donation upgrade without active subscription and token');
              return Promise.resolve({
                success: false,
                errors: [{ message: 'Missing active subscription details' }],
              });
            }
            return submitDonationUpgrade(
              formData,
              state.formDetails,
              state.activeSubscription,
              state.upgradeJwt,
              state.useNewPaymentMethod,
              state.utmCampaign
            );
          })()
        : submitDonation(formData, state.formDetails, state.utmCampaign, {
            memberId: state.memberId,
            withAuth: Boolean(state.memberId) || state.disableCaptcha,
            sfmcSubkey: state.sfmcSubkey,
          }));

      if (result.success) {
        // Handle normal receipt display for other payment methods
        if (result.receipt) {
          const rawAmount = formData.amount ?? formData.customAmount ?? 0;
          const annualizedValue = formData.frequency === 'monthly' ? rawAmount * 12 : rawAmount;
          purchaseEvent(result.receipt.receiptId, annualizedValue, state.placement);

          dispatch(setReceipt(result.receipt));
          logger.info('Donation submitted successfully', {
            receiptId: result.receipt.receiptId,
            amount: result.receipt.amount,
            frequency: formData.frequency,
          });
        }
      } else {
        // Handle submission errors
        logger.error('Donation submission failed', { 
          errors: result.errors,
        });

        const submissionErrorResult = getPrimarySubmissionError(
          result.errors,
          SUBMISSION_ERROR_FALLBACK_MESSAGE
        );
        dispatch(setSubmissionError(submissionErrorResult));
        dispatch(setSubmitting(false));
        if (isPaypalRest && submissionErrorResult.code === 'PAYPAL_COMPLETION_UNCERTAIN') {
          dispatch({ type: 'SET_PAYPAL_UNCERTAIN' });
        } else if (isPaypalRest && (isPaymentFailureError(result.errors) || submissionErrorResult.code === '4003')) {
          dispatch({ type: 'CLEAR_PAYPAL_APPROVAL' });
          dispatch({ type: 'SET_STEP', payload: 'payment' });
        } else if (isPaymentFailureError(result.errors)) {
          if (formData.paymentMethod === 'card') {
            // Discard the single-use token and let the donor read the error before choosing again.
            dispatch(setPaymentMethod(''));
          }
          dispatch({ type: 'SET_STEP', payload: 'payment' });
        }
        
        // Reset captcha on submission failure
        if (captchaRef.current) {
          void captchaRef.current.reset().catch(() => {
            // Ignore reset errors
          });
        }
      }
    } catch (error) {
      logger.error('Failed to submit donation', { error });
      
      dispatch(
        setSubmissionError({
          message: error instanceof Error ? error.message : SUBMISSION_ERROR_FALLBACK_MESSAGE,
        })
      );
      dispatch(setSubmitting(false));
      
      // Reset captcha on submission failure
      if (captchaRef.current) {
        void captchaRef.current.reset().catch(() => {
          // Ignore reset errors
        });
      }
    } finally {
      submissionInFlight.current = false;
    }
  };

  const currentAmount = formData.totalDonationAmount ?? formData.amount ?? formData.customAmount ?? 0
  const showBackButton = !showStepNavigator;
  const submissionErrorMessage = getSubmissionErrorMessage(submissionError);
  
  return (
    <VStack gap={6} align="stretch">
      {/* Contact Info Section */}
      <Box textAlign="center">
        <Text fontSize="md" fontWeight="semibold" mb={1.5}>
          {isPaypalRest ? (formData.firstName ? `Almost done, ${formData.firstName}` : 'Almost done') : 'Contact Info'}
        </Text>
        <Text fontSize="xs" fontStyle="italic" color="fg.muted">
          {isPaypalRest ? 'Approved with PayPal. Confirm your information to complete your donation.' : "We'll need to send you a receipt."}
        </Text>
      </Box>
      {submissionErrorMessage ? (
        <Box bg="red.subtle" borderRadius="md" borderWidth={1} borderColor="border" p={3}>
          <Text fontSize="sm" fontWeight="semibold" color="red.fg">
            {submissionErrorMessage}
          </Text>
        </Box>
      ) : null}

      {/* Contact Form Fields */}
      <VStack gap={3.5} align="stretch">
        <HStack gap={3} align="start">
          <Field.Root flex={1} invalid={!!fieldErrors.firstName}>
            <Field.Label fontSize="sm" fontWeight="semibold">
              First Name <Text as="span" color="red.fg" fontSize="2xs">*</Text>
            </Field.Label>
            <Input
              value={formData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={(e) => handleFieldBlur('firstName', e.target.value)}
              size="sm"
            />
            {fieldErrors.firstName && (
              <Field.ErrorText fontSize="xs">{fieldErrors.firstName}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root flex={1} invalid={!!fieldErrors.lastName}>
            <Field.Label fontSize="sm" fontWeight="semibold">
              Last Name <Text as="span" color="red.fg" fontSize="2xs">*</Text>
            </Field.Label>
            <Input
              value={formData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={(e) => handleFieldBlur('lastName', e.target.value)}
              size="sm"
            />
            {fieldErrors.lastName && (
              <Field.ErrorText fontSize="xs">{fieldErrors.lastName}</Field.ErrorText>
            )}
          </Field.Root>
        </HStack>

        <Field.Root invalid={!!fieldErrors.email}>
          <Field.Label fontSize="sm" fontWeight="semibold">
            Email{isEmailRequired ? <Text as="span" color="red.fg" fontSize="2xs"> *</Text> : null}
          </Field.Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            onBlur={(e) => handleFieldBlur('email', e.target.value)}
            size="sm"
          />
          {fieldErrors.email && (
            <Field.ErrorText fontSize="xs">{fieldErrors.email}</Field.ErrorText>
          )}
        </Field.Root>

        {!isPaypalRest && <Field.Root>
          <Field.Label fontSize="sm" fontWeight="semibold">
            Phone Number
          </Field.Label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            onBlur={(e) => handlePhoneBlur(e.target.value)}
            placeholder="Optional"
            size="sm"
          />
        </Field.Root>}
      </VStack>

      {/* Shipping Address Section - Show if gift selected or always_show_address is set */}
      {showAddressSection && (
        <>
          <Box textAlign="center" mt={2}>
            <Text fontSize="md" fontWeight="semibold" mb={1.5}>
              Shipping Address
            </Text>
            <Text fontSize="xs" fontStyle="italic" color="fg.muted">
              {hasGift ? 'Where should we send your gift?' : 'Please confirm your mailing address.'}
            </Text>
          </Box>

          <VStack gap={3.5} align="stretch">
            <Field.Root invalid={!!fieldErrors.street}>
              <Field.Label fontSize="sm" fontWeight="semibold">
                Street <Text as="span" color="red.fg" fontSize="2xs">*</Text>
              </Field.Label>
              <Input
                value={formData.street}
                onChange={(e) => handleFieldChange('street', e.target.value)}
                onBlur={(e) => handleFieldBlur('street', e.target.value)}
                size="sm"
              />
              {fieldErrors.street && (
                <Field.ErrorText fontSize="xs">{fieldErrors.street}</Field.ErrorText>
              )}
            </Field.Root>

            <HStack gap={3} align="start">
              <Field.Root flex={1} invalid={!!fieldErrors.city}>
                <Field.Label fontSize="sm" fontWeight="semibold">
                  City <Text as="span" color="red.fg" fontSize="2xs">*</Text>
                </Field.Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  onBlur={(e) => handleFieldBlur('city', e.target.value)}
                  size="sm"
                />
                {fieldErrors.city && (
                  <Field.ErrorText fontSize="xs">{fieldErrors.city}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root flex={1} invalid={!!fieldErrors.state}>
                <Field.Label fontSize="sm" fontWeight="semibold">
                  {formData.country === 'Canada' ? 'Province' : 'State'} <Text as="span" color="red.fg" fontSize="2xs">*</Text>
                </Field.Label>
                <NativeSelectRoot size="sm">
                  <NativeSelectField
                    value={formData.state}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      handleFieldChange('state', e.target.value);
                      handleFieldBlur('state', e.target.value);
                    }}
                  >
                    <option value="">Select {formData.country === 'Canada' ? 'province' : 'state'}</option>
                    {stateOptions.map((stateCode) => (
                      stateCode && <option key={stateCode} value={stateCode}>{stateCode}</option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
                {fieldErrors.state && (
                  <Field.ErrorText fontSize="xs">{fieldErrors.state}</Field.ErrorText>
                )}
              </Field.Root>
            </HStack>

            <HStack gap={3} align="start">
              <Field.Root flex={1} invalid={!!fieldErrors.country}>
                <Field.Label fontSize="sm" fontWeight="semibold">
                  Country <Text as="span" color="red.fg" fontSize="2xs">*</Text>
                </Field.Label>
                <NativeSelectRoot size="sm">
                  <NativeSelectField
                    value={formData.country}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      handleFieldChange('country', e.target.value);
                      handleFieldBlur('country', e.target.value);
                    }}
                  >
                    {countryOptions.map((country) => (
                      <option key={country.value} value={country.label}>{country.label}</option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
                {fieldErrors.country && (
                  <Field.ErrorText fontSize="xs">{fieldErrors.country}</Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root flex={1} invalid={!!fieldErrors.postalCode}>
                <Field.Label fontSize="sm" fontWeight="semibold">
                  Postal Code <Text as="span" color="red.fg" fontSize="2xs">*</Text>
                </Field.Label>
                <Input
                  value={formData.postalCode}
                  onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                  onBlur={(e) => handleFieldBlur('postalCode', e.target.value)}
                  size="sm"
                />
                {fieldErrors.postalCode && (
                  <Field.ErrorText fontSize="xs">{fieldErrors.postalCode}</Field.ErrorText>
                )}
              </Field.Root>
            </HStack>
          </VStack>
        </>
      )}

      {/* Newsletter Opt-in */}
      {state.formDetails?.show_email_opt_in && !isMonthlyUpgrade && (
        <Checkbox.Root
          checked={formData.newsletterOptIn}
          onCheckedChange={(details) => handleFieldChange('newsletterOptIn', details.checked === true)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>
            <Text fontSize="xs" textAlign="left" lineHeight="1.2">
              Yes, I'd like to stay in touch with KUOW!<br />
              Please send me emails about programming, member perks, and how I can power public radio.
            </Text>
          </Checkbox.Label>
        </Checkbox.Root>
      )}

      {/* reCAPTCHA */}
      {requiresCaptcha && (
        <Box>
          <Recaptcha
            ref={captchaRef}
            action="donation"
            onVerify={handleCaptchaVerified}
            onError={handleCaptchaError}
          />
          {fieldErrors.captcha && (
            <Text fontSize="xs" color="red.fg" mt={1}>
              {fieldErrors.captcha}
            </Text>
          )}
        </Box>
      )}

      <DonationContinueSection
        amount={currentAmount}
        frequency={formData.frequency}
        buttonLabel="Complete Donation"
        onClick={() => {
          void handleSubmit();
        }}
        disabled={isSubmitting || Boolean(formData.paypalCompletionUncertain) || !canContinueFromStep('information', validation) || (isPaypalRest && !validation.payment)}
        loading={isSubmitting}
        loadingText={processingMessage}
        showArrow={false}
        variant="solid"
      />

      {showBackButton ? (
        <Button
          variant="subtle"
          size="xs"
          onClick={() => dispatch(prevStep())}
          alignSelf="center"
        >
          Back
        </Button>
      ) : null}

      <HStack justify="center" gap={6} pt={4}>
        <Image
          src={verisignLogo}
          alt="VeriSign Secured"
          h="45px"
          w="80px"
          objectFit="contain"
        />
        <Image
          src={charityLogo}
          alt="Charity Navigator"
          h="32px"
          w="125px"
          objectFit="contain"
        />
      </HStack>
    </VStack>
  );
};
